import {
  Component,
  ViewChild,
  ViewContainerRef,
  computed,
  effect,
  input,
  output,
} from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { AssetInfo } from '@app/@shared/models/pdf-asset-info';
import {
  CustomOption,
  OptionGroup,
} from '../generic-toolbar/option-model';
import { StatelessBehavior } from '../generic-toolbar/state-behavior-model';
import { Icon } from '../generic-toolbar/icons';
import { DialogService } from '@app/@shared/services/dialog/dialog.service';
import { ShareItemModalComponent } from '../share-item-modal/share-item-modal.component';
import { ToolbarTypes } from '../generic-toolbar/toolbar-type';
import { PdfViewerComponent } from 'ng2-pdf-viewer';
import { FeatureFlagService, ffKeys } from '@app/@shared/services/featureflag.service';

@Component({
    selector: 'app-alt-pdf-viewer',
    templateUrl: './alt-pdf-viewer.component.html',
    styleUrl: './alt-pdf-viewer.component.scss',
    standalone: false
})
export class AltPdfViewerComponent {
  pdfAssetInfo = input<AssetInfo>();
  showDownloadButton = input<boolean>();
  showCloseButton = input<boolean>();
  downloadPdf = output<void>();
  closePdfViewer = output<void>();
  toolbarOptions: OptionGroup[];
  url = computed<SafeUrl>(() => this.pdfAssetInfo()?.signedUrl);
  toolbarType = ToolbarTypes.OTHER;
  zoomLevel = 1;
  searchTerm: string;
  matchCount = 0;
  currentMatch = 0;
  searchEnabled = this.ffService.getFlag(ffKeys.enablePdfSearch);

  @ViewChild(PdfViewerComponent) private pdfViewer: PdfViewerComponent;

  constructor(
    private readonly viewRef: ViewContainerRef,
    private readonly dialogService: DialogService,
    private readonly ffService: FeatureFlagService,
  ) {
    effect(() => {
        this.toolbarOptions = this.createToolbarOptions();
    });
    }

  createToolbarOptions(): OptionGroup[] {
    const zoomOptions: any[] = [];
    zoomOptions.push(
      new CustomOption(
        'zoom-out',
        { click: this.zoomOut.bind(this) },
        new StatelessBehavior(Icon.REPORT_VIEWER_ZOOM_OUT, 'Zoom Out'),
      ),
    );

    zoomOptions.push(
      new CustomOption(
        'zoom-in',
        { click: this.zoomIn.bind(this) },
        new StatelessBehavior(Icon.REPORT_VIEWER_ZOOM_IN, 'Zoom In'),
      ),
    );

    zoomOptions.push(
      new CustomOption(
        'toggle-fullscreen',
        { click: this.toggleFullScreen.bind(this) },
        new StatelessBehavior(
          Icon.REPORT_VIEWER_ENTER_FULLSCREEN,
          'Full Screen',
        ),
      ),
    );

    const otherOptions: any[] = [];
    otherOptions.push(
      new CustomOption(
        'share',
        { click: this.share.bind(this) },
        new StatelessBehavior(Icon.IMAGE_SHARE, 'Share'),
      ),
    );

    if (this.showDownloadButton() && this.pdfAssetInfo().downloadUrl) {
      otherOptions.push(
        new CustomOption(
          'download',
          { click: this.download.bind(this) },
          new StatelessBehavior(Icon.REPORT_VIEWER_DOWNLOAD, ''),
        ),
      );
    }

    const closeOption: any[] = [];
    if (this.showCloseButton()) {
      closeOption.push(
        new CustomOption(
          'close-viewer',
          { click: this.close.bind(this) },
          new StatelessBehavior(Icon.REPORT_VIEWER_CLOSE, 'Close'),
          'Close',
        )
      );
    }


    return [new OptionGroup(zoomOptions), new OptionGroup(otherOptions), new OptionGroup(closeOption)];
  }

  zoomIn() {
    this.zoomLevel = Math.min(this.zoomLevel + 0.1, 3);
  }

  zoomOut() {
    this.zoomLevel = Math.max(this.zoomLevel - 0.1, 0.5);
  }

  toggleFullScreen(): void {
    const elem = this.viewRef.element.nativeElement;

    if (!document.fullscreenElement) {
      elem.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  share(): void {
    const shareFormModalInstanse = this.dialogService.open(
      ShareItemModalComponent,
      { windowClass: 'shareModalClass' },
    );
    shareFormModalInstanse.componentInstance.clObjectId =
      this.pdfAssetInfo().clObjectId;
    shareFormModalInstanse.componentInstance.isDocumentShareable = true;
    shareFormModalInstanse.componentInstance.shareEntityType = 'ASSET';
  }

  download(): void {
    this.downloadPdf.emit();
  }

  close() {
    this.closePdfViewer.emit();
  }

  search(action: boolean | undefined = undefined) {
    this.pdfViewer.eventBus.dispatch('find', {
      query: this.searchTerm,
      type: 'again',
      caseSensitive: false,
      findPrevious: action,
      higlightAll: true,
      phraseSearch: true
    });
  }

  onPdfLoaded() {
    const eventBus = this.pdfViewer.eventBus;

    eventBus.on('updatefindmatchescount', (e: any) => {
      this.matchCount = e.matchesCount.total;
      this.currentMatch = e.matchesCount.current;
    });

    eventBus.on('updatefindcontrolstate', (e: any) => {
      if(e.state === 0) {
        this.currentMatch = e.matchesCount.current;
      } else if(e.state === 1) {
        this.currentMatch = 0;
        this.matchCount = 0;
      }
    });
  }
}
